// apps/smart-shikka-web/src/pages/chat/useVoiceSession.ts
// Voice runtime state + handlers for the Chat page — consent, recording,
// wake-word, playback, and the unprivileged browser SpeechRecognition path.
// Extracted verbatim from pages/Chat.tsx (CORE-14 modularization; behavior
// identical — every callback keeps its original body and dep list).
import { useCallback, useEffect, useRef, useState } from "react";
import {
  safeErrorCode,
  safeErrorText,
  type CeoActivityState,
  type CeoApprovalRequest,
  type ProductLocale,
  type VoiceRecordingMetadata,
  type VoiceRecordingState,
} from "@smart-shikka/react";
import type { createVoiceService, createWorkspaceCeoService } from "@smart-shikka/react";
import { createVoiceSessionId } from "./chatApi.js";

type VoiceService = ReturnType<typeof createVoiceService>;
type CeoService = ReturnType<typeof createWorkspaceCeoService>;

export interface VoiceSessionDeps {
  readonly isPrivileged: boolean;
  readonly token: string | null;
  readonly accountRole: string | undefined;
  readonly locale: ProductLocale;
  readonly tr: (key: string) => string;
  readonly activeId: string | null;
  readonly voiceService: VoiceService;
  readonly ceoService: CeoService;
  /** UX-05 — canonical `voice` namespace `preferred_voice_id` preference; undefined keeps the server default. */
  readonly preferredVoiceId?: string | undefined;
  readonly appendMessage: (role: "user" | "assistant", content: string) => void;
  readonly setInput: (updater: (prev: string) => string) => void;
  readonly setStatus: (status: "ready" | "error") => void;
  readonly setApprovalPending: (pending: boolean) => void;
  /** §Q — surface a turn's approval request into the normal chat approval card. */
  readonly onApprovalRequest?: (
    request: CeoApprovalRequest,
    input: string,
    sessionId: string,
  ) => void;
}

export function useVoiceSession(deps: VoiceSessionDeps) {
  const {
    isPrivileged,
    token,
    accountRole,
    locale,
    tr,
    activeId,
    voiceService,
    ceoService,
    preferredVoiceId,
    appendMessage,
    setInput,
    setStatus,
    setApprovalPending,
    onApprovalRequest,
  } = deps;

  const [listening, setListening] = useState(false);
  const [micError, setMicError] = useState<string | null>(null);
  const [voicePanelOpen, setVoicePanelOpen] = useState(false);
  const [voiceConsent, setVoiceConsent] = useState(false);
  const [voiceBusy, setVoiceBusy] = useState(false);
  const [voiceActivity, setVoiceActivity] = useState<CeoActivityState>("idle");
  const [voiceAudioSrc, setVoiceAudioSrc] = useState<string | null>(null);
  const [foregroundWakeActive, setForegroundWakeActive] = useState(false);
  const [voiceRecordingActive, setVoiceRecordingActive] = useState(false);
  const [voiceSessionId] = useState(createVoiceSessionId);
  const voiceRequestControllerRef = useRef<AbortController | null>(null);
  const voiceRequestIdRef = useRef(0);
  const voiceSurfaceEnabledRef = useRef(false);

  useEffect(() => {
    setVoiceConsent(false);
    setVoiceAudioSrc(null);
    setForegroundWakeActive(false);
    setVoiceRecordingActive(false);
    setVoiceActivity("idle");
    voiceSurfaceEnabledRef.current = false;
  }, [token]);

  const cancelVoicePlayback = useCallback(() => {
    setVoiceAudioSrc(null);
    setVoiceActivity((current) => (current === "speaking" ? "idle" : current));
  }, []);

  const beginVoiceRequest = useCallback(() => {
    voiceRequestControllerRef.current?.abort();
    const controller = new AbortController();
    voiceRequestControllerRef.current = controller;
    const requestId = voiceRequestIdRef.current + 1;
    voiceRequestIdRef.current = requestId;
    return { controller, requestId };
  }, []);

  const cancelVoiceInteraction = useCallback(() => {
    voiceRequestIdRef.current += 1;
    voiceRequestControllerRef.current?.abort();
    voiceRequestControllerRef.current = null;
    setVoiceAudioSrc(null);
    setVoiceRecordingActive(false);
    setVoiceBusy(false);
    setVoiceActivity("idle");
  }, []);

  useEffect(() => {
    return () => {
      voiceRequestIdRef.current += 1;
      voiceRequestControllerRef.current?.abort();
      voiceRequestControllerRef.current = null;
      voiceSurfaceEnabledRef.current = false;
    };
  }, []);

  const enableVoice = useCallback(async () => {
    if (!isPrivileged || !token || voiceBusy) return;
    setMicError(null);
    setVoiceBusy(true);
    setVoiceActivity("thinking");
    const result = await voiceService.grantConsent(voiceSessionId);
    if (result.success) {
      setVoiceConsent(true);
      voiceSurfaceEnabledRef.current = true;
      setVoiceActivity("idle");
    } else {
      // Raw backend detail never renders — identifier codes only.
      setMicError(safeErrorText(safeErrorCode(result.error?.code ?? result.error?.message), locale).title);
      setVoiceActivity("error");
    }
    setVoiceBusy(false);
  }, [isPrivileged, token, voiceBusy, voiceService, voiceSessionId, locale]);

  const handleVoiceRecordingState = useCallback((state: VoiceRecordingState) => {
    if (state === "requesting_permission" || state === "recording" || state === "stopping") {
      setVoiceRecordingActive(true);
    } else {
      setVoiceRecordingActive(false);
    }
    if (state === "recording") cancelVoicePlayback();
    if (state === "recording") setVoiceActivity("listening");
    else if (state === "requesting_permission" || state === "stopping") setVoiceActivity("transcribing");
    else if (state === "error") setVoiceActivity("error");
  }, [cancelVoicePlayback]);

  const handleVoiceRecordingRequested = useCallback(() => {
    setVoiceRecordingActive(true);
    cancelVoicePlayback();
  }, [cancelVoicePlayback]);

  const handleVoiceRecording = useCallback(
    async (recording: VoiceRecordingMetadata) => {
      if (!voiceSurfaceEnabledRef.current || !voiceConsent || voiceBusy) return;
      cancelVoicePlayback();
      const { controller, requestId } = beginVoiceRequest();
      setMicError(null);
      setVoiceBusy(true);
      setVoiceActivity("transcribing");
      try {
        const result = await voiceService.turn(recording.blob, {
          sessionId: voiceSessionId,
          language: locale === "hi" ? "hi-IN" : "en-IN",
          durationSeconds: Math.max(0.25, recording.durationMs / 1000),
          signal: controller.signal,
          // CORE-08 — voice turns share the active conversation ledger; the
          // transcript (not raw audio) is the durable evidence.
          ...(activeId ? { conversationId: activeId } : {}),
          idempotencyKey: crypto.randomUUID(),
        });
        if (requestId !== voiceRequestIdRef.current || controller.signal.aborted) return;
        if (!result.success || !result.data) {
          setMicError(safeErrorText(safeErrorCode(result.error?.code ?? result.error?.message), locale).title);
          setVoiceActivity("error");
          return;
        }

        const data = result.data;
        if (data.transcript) appendMessage("user", data.transcript);
        if (data.answer) appendMessage("assistant", data.answer);
        setApprovalPending(data.requiresApproval === true);
        if (
          data.requiresApproval === true &&
          data.approvalRequest &&
          typeof data.approvalRequest["approvalId"] === "string"
        ) {
          onApprovalRequest?.(data.approvalRequest as unknown as CeoApprovalRequest, data.transcript ?? "", voiceSessionId);
        }
        setStatus(result.success ? "ready" : "error");

        const tts = data.tts;
        const audioBase64 =
          tts && "audioBase64" in tts && typeof tts.audioBase64 === "string" ? tts.audioBase64 : undefined;
        const contentType =
          tts && "contentType" in tts && typeof tts.contentType === "string" ? tts.contentType : undefined;
        const allowedContentTypes = new Set(["audio/wav", "audio/mpeg", "audio/ogg"]);
        if (audioBase64 && contentType && allowedContentTypes.has(contentType)) {
          setVoiceAudioSrc(`data:${contentType};base64,${audioBase64}`);
          setVoiceActivity(data.requiresApproval ? "waiting_for_approval" : "speaking");
        } else {
          setVoiceActivity(data.requiresApproval ? "waiting_for_approval" : result.success ? "idle" : "error");
        }
      } finally {
        if (requestId === voiceRequestIdRef.current) {
          voiceRequestControllerRef.current = null;
          setVoiceBusy(false);
        }
      }
    },
    [
      activeId,
      appendMessage,
      beginVoiceRequest,
      cancelVoicePlayback,
      locale,
      onApprovalRequest,
      setApprovalPending,
      setStatus,
      voiceBusy,
      voiceConsent,
      voiceService,
      voiceSessionId,
    ],
  );

  const handleForegroundWakeWord = useCallback(() => {
    cancelVoicePlayback();
    setVoiceActivity("listening");
  }, [cancelVoicePlayback]);

  const handleForegroundWakeState = useCallback((state: string) => {
    if (state === "listening") setVoiceActivity("listening");
  }, []);

  const handleForegroundWakeCommand = useCallback(
    async (query: string) => {
      const content = query.trim();
      if (!voiceSurfaceEnabledRef.current || !content || !voiceConsent || voiceBusy) return;

      cancelVoicePlayback();
      const { controller, requestId } = beginVoiceRequest();
      setMicError(null);
      setVoiceBusy(true);
      setVoiceActivity("thinking");
      appendMessage("user", content);

      try {
        const result = await ceoService.turn(
          {
            input: content,
            voice: true,
            surface: "web",
            role: String(accountRole ?? "user"),
            locale,
            sessionId: voiceSessionId,
            // CORE-08 — voice turns share the active conversation ledger.
            ...(activeId ? { conversationId: activeId } : {}),
            idempotencyKey: crypto.randomUUID(),
          },
          controller.signal,
        );
        if (requestId !== voiceRequestIdRef.current || controller.signal.aborted) return;

        if (!result.ok) {
          setStatus("error");
          setMicError(safeErrorText(safeErrorCode(result.errorCode), locale).title);
          setVoiceActivity("error");
          return;
        }

        if (result.answer) appendMessage("assistant", result.answer);
        setApprovalPending(result.requiresApproval === true);
        if (result.requiresApproval === true && result.approvalRequest?.approvalId) {
          onApprovalRequest?.(result.approvalRequest, content, voiceSessionId);
        }
        setStatus("ready");

        const spokenText = result.spokenAnswer || result.answer;
        if (!spokenText.trim()) {
          setVoiceActivity(result.requiresApproval ? "waiting_for_approval" : "idle");
          return;
        }
        const tts = await voiceService.synthesize(
          spokenText,
          preferredVoiceId,
          voiceSessionId,
          controller.signal,
        );
        if (requestId !== voiceRequestIdRef.current || controller.signal.aborted) return;
        const audioBase64 =
          tts.success && tts.data && typeof tts.data.audioBase64 === "string"
            ? tts.data.audioBase64
            : undefined;
        const contentType =
          tts.success && tts.data && typeof tts.data.contentType === "string"
            ? tts.data.contentType
            : undefined;
        const allowedContentTypes = new Set(["audio/wav", "audio/mpeg", "audio/ogg"]);
        if (audioBase64 && contentType && allowedContentTypes.has(contentType)) {
          setVoiceAudioSrc(`data:${contentType};base64,${audioBase64}`);
          setVoiceActivity(result.requiresApproval ? "waiting_for_approval" : "speaking");
        } else {
          setVoiceActivity(result.requiresApproval ? "waiting_for_approval" : "idle");
        }
      } finally {
        if (requestId === voiceRequestIdRef.current) {
          voiceRequestControllerRef.current = null;
          setVoiceBusy(false);
        }
      }
    },
    [
      accountRole,
      activeId,
      appendMessage,
      beginVoiceRequest,
      cancelVoicePlayback,
      ceoService,
      locale,
      preferredVoiceId,
      setApprovalPending,
      setStatus,
      voiceBusy,
      voiceConsent,
      voiceService,
      voiceSessionId,
    ],
  );

  const handleForegroundWakeCommandDetected = useCallback((query: string) => {
    void handleForegroundWakeCommand(query);
  }, [handleForegroundWakeCommand]);

  const toggleVoice = useCallback(() => {
    setMicError(null);
    if (isPrivileged) {
      if (voicePanelOpen) {
        voiceSurfaceEnabledRef.current = false;
        setForegroundWakeActive(false);
        cancelVoiceInteraction();
      } else if (voiceConsent) {
        voiceSurfaceEnabledRef.current = true;
      }
      setVoicePanelOpen((open) => !open);
      return;
    }
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SR) { setMicError(tr("voiceNotSupported")); return; }
    if (listening) { setListening(false); return; }
    const recognition = new SR();
    recognition.lang = locale === "hi" ? "hi-IN" : "en-US";
    recognition.interimResults = false;
    recognition.onstart = () => setListening(true);
    recognition.onend = () => setListening(false);
    recognition.onerror = (event: any) => {
      if (event.error !== "aborted") setMicError(safeErrorText(safeErrorCode(event.error), locale).title);
      setListening(false);
    };
    recognition.onresult = (event: any) => {
      const transcript = event.results?.[0]?.[0]?.transcript ?? "";
      if (transcript) setInput((prev) => (prev ? `${prev} ${transcript}` : transcript));
    };
    recognition.start();
  }, [cancelVoiceInteraction, isPrivileged, listening, locale, setInput, tr, voicePanelOpen, voiceConsent]);

  return {
    listening,
    micError,
    setMicError,
    voicePanelOpen,
    voiceConsent,
    voiceBusy,
    voiceActivity,
    setVoiceActivity,
    voiceAudioSrc,
    setVoiceAudioSrc,
    foregroundWakeActive,
    setForegroundWakeActive,
    voiceRecordingActive,
    voiceSessionId,
    enableVoice,
    cancelVoicePlayback,
    cancelVoiceInteraction,
    handleVoiceRecordingState,
    handleVoiceRecordingRequested,
    handleVoiceRecording,
    handleForegroundWakeWord,
    handleForegroundWakeState,
    handleForegroundWakeCommandDetected,
    toggleVoice,
  };
}
